import { useMemo, useState } from "react";
import { useMutation } from "@apollo/client/react";
import { CreateConversationDocument } from "@/graphql/generated/operations";
import { useChatStore } from "@/stores/chat-store";

// Shared "message this person" logic: reuse the existing 1:1 conversation if one
// exists (the store is seeded globally by ChatDock's MyConversations), otherwise
// create one – re-checking the store after creation so a late MyConversations
// result can't leave two windows for the same person. Used by the topbar search
// hits and the profile page chat action.
export function useStartChat(personId: string) {
  const [busy, setBusy] = useState(false);
  const [createConversation] = useMutation(CreateConversationDocument);
  const openChat = useChatStore((s) => s.openChat);
  const addConversation = useChatStore((s) => s.addConversation);
  const conversations = useChatStore((s) => s.conversations);
  const myId = useChatStore((s) => s.myId);

  const existingChatId = useMemo(() => {
    for (const c of conversations.values()) {
      const others = c.participantIds.filter((id) => id !== myId);
      if (others.length === 1 && others[0] === personId) return c.id;
    }
    return null;
  }, [conversations, myId, personId]);

  async function startChat() {
    if (busy || !personId) return;
    if (existingChatId) {
      openChat(existingChatId);
      return;
    }
    setBusy(true);
    try {
      const res = await createConversation({
        variables: { input: { participantIds: [personId] } },
      });
      const conv = res.data?.createConversation;
      if (conv?.id) {
        const store = useChatStore.getState();
        const dupe = [...store.conversations.values()].find((c) => {
          const others = c.participantIds.filter((id) => id !== store.myId);
          return others.length === 1 && others[0] === personId && c.id !== conv.id;
        });
        const id = dupe?.id ?? conv.id;
        if (!store.conversations.has(conv.id)) {
          addConversation({ id: conv.id, participantIds: conv.participantIds, unreadCount: 0 });
        }
        openChat(id);
      }
    } finally {
      setBusy(false);
    }
  }

  return { hasChat: existingChatId !== null, busy, startChat };
}
